import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { collection, getDocs, deleteDoc, doc } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Typography,
  Button,
  Chip,
  TablePagination,
  TableSortLabel,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Stack,
  TextField,
  InputAdornment,
} from "@mui/material";
import {
  Visibility,
  Edit,
  Delete,
  Add as AddIcon,
  Search as SearchIcon,
  PictureAsPdf as PictureAsPdfIcon,
} from "@mui/icons-material";

const EntryList = () => {
  const [data, setData] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedIds, setSelectedIds] = useState([]);
  const [openBulkDeleteDialog, setOpenBulkDeleteDialog] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");
  const { userRole } = useAuth();
  const isEditor = userRole === "editor";

  useEffect(() => {
    const fetchEntries = async () => {
      const querySnapshot = await getDocs(collection(db, "entries"));
      const entries = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setData(entries);
    };

    fetchEntries();
  }, []);

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this entry?")) {
      await deleteDoc(doc(db, "entries", id));
      setData(data.filter((entry) => entry.id !== id));
      setSelectedIds((prev) => prev.filter((selectedId) => selectedId !== id));
    }
  };

  const getSearchableText = (value) => {
    if (value === null || value === undefined) return "";
    if (typeof value === "string") return value;
    if (typeof value === "number" || typeof value === "boolean") {
      return String(value);
    }
    if (Array.isArray(value)) {
      return value.map(getSearchableText).join(" ");
    }
    if (typeof value === "object") {
      return Object.values(value).map(getSearchableText).join(" ");
    }
    return "";
  };

  const filteredEntries = data.filter((entry) => {
    if (!searchQuery.trim()) return true;
    return getSearchableText(entry)
      .toLowerCase()
      .includes(searchQuery.toLowerCase());
  });

  const getSortValue = (entry, key) => {
    if (key === "author") {
      return entry.Author?.name || "";
    }
    if (key === "title") {
      return entry.Title?.full_title || "";
    }
    return "";
  };

  const compareValues = (a, b) => {
    const aEmpty = a === null || a === undefined || a === "";
    const bEmpty = b === null || b === undefined || b === "";

    if (aEmpty && bEmpty) return 0;
    if (aEmpty) return 1;
    if (bEmpty) return -1;

    return String(a).localeCompare(String(b), undefined, {
      numeric: true,
      sensitivity: "base",
    });
  };

  const sortedEntries = [...filteredEntries].sort((a, b) => {
    if (!sortBy) return 0;
    const compared = compareValues(getSortValue(a, sortBy), getSortValue(b, sortBy));
    return sortDirection === "asc" ? compared : -compared;
  });

  const currentPageEntries = sortedEntries.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  const currentPageIds = currentPageEntries.map((entry) => entry.id);
  const selectedOnPageCount = currentPageIds.filter((id) =>
    selectedIds.includes(id),
  ).length;
  const allCurrentPageSelected =
    currentPageIds.length > 0 && selectedOnPageCount === currentPageIds.length;
  const isCurrentPageIndeterminate =
    selectedOnPageCount > 0 && selectedOnPageCount < currentPageIds.length;

  const handleToggleSelectOne = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id)
        ? prev.filter((selectedId) => selectedId !== id)
        : [...prev, id],
    );
  };

  const handleToggleSelectPage = (checked) => {
    if (checked) {
      setSelectedIds((prev) => [...new Set([...prev, ...currentPageIds])]);
      return;
    }

    setSelectedIds((prev) =>
      prev.filter((selectedId) => !currentPageIds.includes(selectedId)),
    );
  };

  const handleBulkDelete = async () => {
    await Promise.all(
      selectedIds.map((id) => deleteDoc(doc(db, "entries", id))),
    );
    setData((prev) => prev.filter((entry) => !selectedIds.includes(entry.id)));
    setSelectedIds([]);
    setOpenBulkDeleteDialog(false);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    setPage(0);
  };

  const handleRequestSort = (columnKey) => {
    const isSameColumn = sortBy === columnKey;
    if (isSameColumn) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
      return;
    }

    setSortBy(columnKey);
    setSortDirection("asc");
  };

  return (
    <Box sx={{ p: 3, backgroundColor: "#f5f5f5", minHeight: "100vh" }}>
      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: { xs: "stretch", sm: "center" },
            flexDirection: { xs: "column", sm: "row" },
            gap: 2,
            mb: 3,
          }}
        >
          <Typography
            variant="h3"
            component="h1"
            sx={{
              color: "#1976d2",
              fontWeight: "bold",
              fontSize: { xs: "2.2rem", sm: "3rem" },
              lineHeight: 1.1,
            }}
          >
            Women's Early Printed Fiction Database
          </Typography>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            sx={{ width: { xs: "100%", sm: "auto" } }}
          >
            {isEditor && (
              <>
                <Button
                  variant="contained"
                  color="error"
                  startIcon={<Delete />}
                  disabled={selectedIds.length === 0}
                  onClick={() => setOpenBulkDeleteDialog(true)}
                  sx={{ width: { xs: "100%", sm: "auto" } }}
                >
                  Delete Selected ({selectedIds.length})
                </Button>
                <Button
                  component={Link}
                  to="/new"
                  variant="contained"
                  color="primary"
                  size="large"
                  startIcon={<AddIcon />}
                  sx={{ width: { xs: "100%", sm: "auto" } }}
                >
                  Create New Entry
                </Button>
              </>
            )}
          </Stack>
        </Box>
        <TextField
          fullWidth
          size="small"
          label="Search all fields"
          value={searchQuery}
          onChange={handleSearchChange}
          sx={{ mt: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      <TableContainer component={Paper} elevation={3}>
        <Table sx={{ minWidth: 560 }}>
          <TableHead sx={{ backgroundColor: "#1976d2" }}>
            <TableRow>
              <TableCell sx={{ color: "white", fontWeight: "bold", width: 50 }}>
                <Checkbox
                  checked={allCurrentPageSelected}
                  indeterminate={isCurrentPageIndeterminate}
                  onChange={(e) => handleToggleSelectPage(e.target.checked)}
                  sx={{ color: "white", "&.Mui-checked": { color: "white" } }}
                />
              </TableCell>
              <TableCell
                sx={{
                  color: "white",
                  fontWeight: "bold",
                  fontSize: { xs: "0.8rem", sm: "0.95rem" },
                }}
              >
                <TableSortLabel
                  active={sortBy === "author"}
                  direction={sortBy === "author" ? sortDirection : "asc"}
                  onClick={() => handleRequestSort("author")}
                  sx={{
                    color: "white",
                    "&.Mui-active": { color: "white" },
                    "& .MuiTableSortLabel-icon": { color: "white !important" },
                  }}
                >
                  Author
                </TableSortLabel>
              </TableCell>
              <TableCell
                sx={{
                  color: "white",
                  fontWeight: "bold",
                  fontSize: { xs: "0.8rem", sm: "0.95rem" },
                }}
              >
                <TableSortLabel
                  active={sortBy === "title"}
                  direction={sortBy === "title" ? sortDirection : "asc"}
                  onClick={() => handleRequestSort("title")}
                  sx={{
                    color: "white",
                    "&.Mui-active": { color: "white" },
                    "& .MuiTableSortLabel-icon": { color: "white !important" },
                  }}
                >
                  Title
                </TableSortLabel>
              </TableCell>
              <TableCell
                sx={{
                  color: "white",
                  fontWeight: "bold",
                  width: 150,
                  fontSize: { xs: "0.8rem", sm: "0.95rem" },
                }}
                align="center"
              >
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {currentPageEntries.map((entry) => (
              <TableRow
                key={entry.id}
                hover
                sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
              >
                <TableCell>
                  <Checkbox
                    checked={selectedIds.includes(entry.id)}
                    onChange={() => handleToggleSelectOne(entry.id)}
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={entry.Author.name || "Unknown"}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                </TableCell>
                <TableCell sx={{ maxWidth: { xs: 170, sm: 420 } }}>
                  <Typography
                    variant="body2"
                    noWrap
                    sx={{
                      fontWeight: 500,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {entry.Title.full_title}
                  </Typography>
                </TableCell>
                <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexWrap: "nowrap",
                      gap: 0.5,
                    }}
                  >
                    <Tooltip title="View Details">
                      <IconButton
                        component={Link}
                        to={`/entry/${entry.id}`}
                        size="small"
                        color="info"
                        sx={{ padding: "4px" }}
                      >
                        <Visibility sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                    {entry.Bibliographical_details?.pdf_url && (
                      <Tooltip title="Open PDF">
                        <IconButton
                          component="a"
                          href={entry.Bibliographical_details.pdf_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          size="small"
                          color="error"
                          sx={{ padding: "4px" }}
                        >
                          <PictureAsPdfIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                    {isEditor && (
                      <>
                        <Tooltip title="Edit Entry">
                          <IconButton
                            component={Link}
                            to={`/edit/${entry.id}`}
                            size="small"
                            color="warning"
                            sx={{ padding: "4px" }}
                          >
                            <Edit sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Entry">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDelete(entry.id)}
                            sx={{ padding: "4px" }}
                          >
                            <Delete sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={filteredEntries.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>

      <Dialog
        open={openBulkDeleteDialog}
        onClose={() => setOpenBulkDeleteDialog(false)}
      >
        <DialogTitle>Delete selected entries</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete {selectedIds.length} selected entr
            {selectedIds.length === 1 ? "y" : "ies"}? This action cannot be
            undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenBulkDeleteDialog(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleBulkDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EntryList;
